import { getLogger } from "@expert/logging";
import type { ContentfulClientApi } from "contentful";
import type { QuizChallenge } from "@/sales-tools/challenges/api/getChallengesForEmployee";
import { getGamificationApiClient } from "@/sales-tools/home-product/api/api";
import type { Answer, Question } from "../../types";
import { getQuestionFromContentful } from "./getContentfulQuestion";

const logger = getLogger({
    module: "questionnaire",
    supportTeam: "sales",
});

export const getSingleQuestionnaireQuestion = async (
    questionId: string,
    client: ContentfulClientApi<undefined>,
): Promise<Question | undefined> => {
    try {
        return await getQuestionFromContentful(questionId, client);
    } catch (e) {
        logger.error(e, "error when fetching question");
    }
};

export const postAnswer = async (question: Answer): Promise<void> => {
    try {
        await getGamificationApiClient().post(`v1/questionnaire`, { json: question });
    } catch (err: unknown) {
        logger.error({ err }, "postQuestionnaire API call failed");
        throw err;
    }
};

export const determineRandomQuestion = (quizChallenge: QuizChallenge) => {
    if (!quizChallenge.metadata || quizChallenge.metadata?.questionList == null) {
        return "";
    }
    const questionIdList = quizChallenge.metadata.questionList.map((question) => question.questionId);
    const answeredQuestions = quizChallenge.answeredQuestions;
    const unanswered = questionIdList
        .filter((question) => !answeredQuestions?.find((answer) => answer.questionId === question))
        .map((question) => question);
    if (unanswered.length > 0) {
        return unanswered[Math.floor(Math.random() * unanswered.length)];
    }
    const incorrectAnswersOnly = questionIdList.filter(
        (question) => !answeredQuestions?.find((answer) => answer.questionId === question && answer.isCorrectAnswer),
    );
    if (incorrectAnswersOnly.length === 0) {
        return "";
    }
    const incorrectCounts: { question: string; count: number }[] = [];
    incorrectAnswersOnly.forEach((question) => {
        const incorrectCount = {
            question: question,
            count:
                answeredQuestions?.filter((answer) => answer.questionId === question && !answer.isCorrectAnswer)
                    .length ?? 0,
        };
        incorrectCounts.push(incorrectCount);
    });
    incorrectCounts.sort((a, b) => a.count - b.count);
    const minAnswersOnly = incorrectCounts.filter((question) => question.count === incorrectCounts[0].count);
    return minAnswersOnly[Math.floor(Math.random() * minAnswersOnly.length)].question;
};
